import React from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, useTheme } from '@mui/material';
import DashboardHeader from './DashboardHeader';
import useMobileView from '../../hooks/useMobileView';
import { mobileAppGlassCard } from '../layout/mobile/mobileAppTheme';
import { getDashboardPageShellSx, getDashboardPaperSx } from '../../utils/dashboardSurfaceSx';

/**
 * Standard page shell — MUI layout replacing legacy Bootstrap dashboards/row/card markup.
 */
const PageLayout = ({
  title,
  navLinks = [],
  showBookingSectionTabs = false,
  showFinanceSectionTabs = false,
  showInventorySectionTabs = false,
  showCustomerSectionTabs = false,
  breadcrumbs = null,
  children,
  showCard = true,
  showBreadcrumbs = true,
  backFallbackPath = '/dashboard/',
  contentPadding,
}) => {
  const theme = useTheme();
  const { isCompact, isNative, layout } = useMobileView();
  const isDark = theme.palette.mode === 'dark';
  const themeMode = isDark ? 'dark' : 'light';
  const useMobileDark = isCompact;
  const cardPaperSx = useMobileDark
    ? mobileAppGlassCard
    : { ...getDashboardPaperSx(themeMode, 'controls'), mb: 0 };
  const innerPadding = contentPadding ?? { xs: 1.5, sm: 2, md: 2.5 };

  return (
    <Box
      className={`page-layout${isNative ? ' page-layout--native' : ''}${useMobileDark ? ' page-layout--mobile-dark' : ''}`}
      sx={{
        ...getDashboardPageShellSx(themeMode),
        ...(isCompact
          ? { px: layout.pagePaddingX, pt: layout.pagePaddingY, pb: layout.pagePaddingY }
          : {}),
        minWidth: 0,
        width: '100%',
        color: useMobileDark ? '#f8fafc' : 'inherit',
      }}
    >
      <Box sx={{ mb: isCompact ? 1.25 : 3, width: '100%', minWidth: 0 }}>
        <DashboardHeader
          title={title}
          navLinks={navLinks}
          showBookingSectionTabs={showBookingSectionTabs}
          showFinanceSectionTabs={showFinanceSectionTabs}
          showInventorySectionTabs={showInventorySectionTabs}
          showCustomerSectionTabs={showCustomerSectionTabs}
          breadcrumbs={breadcrumbs}
          showBreadcrumbs={showBreadcrumbs}
          backFallbackPath={backFallbackPath}
          forceDark={useMobileDark}
        />
      </Box>
      {showCard ? (
        <Paper
          elevation={0}
          sx={{
            ...cardPaperSx,
            overflow: 'hidden',
            color: useMobileDark ? '#f8fafc' : 'inherit',
          }}
        >
          <Box sx={{ p: innerPadding }}>{children}</Box>
        </Paper>
      ) : (
        <Box sx={{ minWidth: 0, width: '100%' }}>{children}</Box>
      )}
    </Box>
  );
};

PageLayout.propTypes = {
  title: PropTypes.string.isRequired,
  navLinks: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  showBookingSectionTabs: PropTypes.bool,
  showFinanceSectionTabs: PropTypes.bool,
  showInventorySectionTabs: PropTypes.bool,
  showCustomerSectionTabs: PropTypes.bool,
  breadcrumbs: PropTypes.oneOfType([PropTypes.array, PropTypes.oneOf([null])]),
  children: PropTypes.node.isRequired,
  showCard: PropTypes.bool,
  showBreadcrumbs: PropTypes.bool,
  backFallbackPath: PropTypes.string,
  contentPadding: PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.array]),
};

export default PageLayout;
